/**
 * RetirementEventsPanel Component
 * WP-RETIRE-8: Main events management panel
 *
 * Features:
 * - Lists existing events grouped by income/expense
 * - "Add Event" button opens event form
 * - Each event row: name, type icon, amount, year/range, edit/delete
 * - Delete confirmation via window.confirm()
 * - Gated behind feature:retirement_events entitlement
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useHasEntitlement } from '@/hooks/useBilling';
import {
  useRetirementEvents,
  useCreateRetirementEvent,
  useUpdateRetirementEvent,
  useDeleteRetirementEvent,
} from '@/hooks/useRetirementPlanning';
import { centsToDollars, formatCurrency } from '@/utils/format';
import type {
  RetirementEvent,
  RetirementEventCreate,
  RetirementEventUpdate,
} from '@/types/retirement';
import RetirementEventForm from './RetirementEventForm';
import CollegePlanForm from './CollegePlanForm';
import {
  frequencyLabel,
  MONEY_IN_HEADING,
  MONEY_OUT_HEADING,
} from './eventKind';

// ============================================================================
// Sub-Components
// ============================================================================

interface EventRowProps {
  event: RetirementEvent;
  onEdit: (event: RetirementEvent) => void;
  onDelete: (id: string) => void;
  isDeleting: boolean;
}

function EventRow({ event, onEdit, onDelete, isDeleting }: EventRowProps) {
  const isIncome = event.amountCents >= 0;
  const displayAmount = formatCurrency(Math.abs(centsToDollars(event.amountCents)));
  const yearRange =
    event.eventType === 'cash_flow' && event.endYear
      ? `${event.year} - ${event.endYear}`
      : `${event.year}`;

  return (
    <div className="flex items-center justify-between py-3 px-4 border-b border-gray-100 last:border-b-0">
      <div className="flex items-center gap-3 min-w-0">
        {/* Type icon */}
        <div
          className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm ${
            isIncome
              ? 'bg-green-100 text-green-700'
              : 'bg-red-100 text-red-700'
          }`}
          title={frequencyLabel(event.eventType)}
        >
          {event.eventType === 'lump_sum' ? (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          )}
        </div>

        {/* Name and details */}
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {event.name}
          </p>
          <p className="text-xs text-gray-500">
            {frequencyLabel(event.eventType)} | {yearRange}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-shrink-0">
        {/* Amount */}
        <span
          className={`text-sm font-medium ${
            isIncome ? 'text-green-700' : 'text-red-700'
          }`}
        >
          {isIncome ? '+' : '-'}{displayAmount}
          {event.eventType === 'cash_flow' && '/yr'}
        </span>

        {/* Edit button */}
        <button
          type="button"
          onClick={() => onEdit(event)}
          className="p-1 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
          aria-label={`Edit ${event.name}`}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </button>

        {/* Delete button */}
        <button
          type="button"
          onClick={() => onDelete(event.id)}
          disabled={isDeleting}
          className="p-1 text-gray-400 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 rounded disabled:opacity-50"
          aria-label={`Delete ${event.name}`}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export default function RetirementEventsPanel() {
  // ---- Entitlement check ----
  const { hasEntitlement, isLoading: entitlementLoading } = useHasEntitlement(
    'feature:retirement_events'
  );

  // ---- Data hooks ----
  const { data: events = [], isLoading: eventsLoading } = useRetirementEvents();
  const createMutation = useCreateRetirementEvent();
  const updateMutation = useUpdateRetirementEvent();
  const deleteMutation = useDeleteRetirementEvent();

  // ---- Local state ----
  const [showForm, setShowForm] = useState(false);
  // WP-RETIRE-13: guided college planning, which generates ordinary events
  const [showCollegeForm, setShowCollegeForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState<RetirementEvent | undefined>(
    undefined
  );

  // ---- Derived values ----
  const incomeEvents = events.filter((e) => e.amountCents >= 0);
  const expenseEvents = events.filter((e) => e.amountCents < 0);

  // ---- Loading ----
  if (entitlementLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse h-8 bg-gray-200 rounded w-1/3" />
      </div>
    );
  }

  // ---- Not entitled: teaser ----
  if (!hasEntitlement) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Plan for college and other life events
        </h3>
        <p className="text-gray-600 mb-4 text-sm">
          Add college costs, a second home, a home sale or any other one-off or
          fixed-period change to your income or spending, and see how each one
          affects your long-term picture.
        </p>
        <Link
          to="/billing"
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          Upgrade to Pro
        </Link>
      </div>
    );
  }

  // ---- Handlers ----

  function handleCreate(data: RetirementEventCreate) {
    createMutation.mutate(data, {
      onSuccess: () => {
        setShowForm(false);
        setEditingEvent(undefined);
      },
    });
  }

  /**
   * WP-RETIRE-13: create one ordinary cash_flow event per child.
   *
   * Events are created sequentially rather than concurrently so that hitting
   * the per-tenant event cap surfaces as a single clear failure instead of a
   * burst of parallel rejections, and so earlier children still persist.
   */
  async function handleCreateCollegePlan(events: RetirementEventCreate[]) {
    for (const event of events) {
      try {
        await createMutation.mutateAsync(event);
      } catch {
        // Stop on the first failure (most likely the event cap); events
        // already created remain, and the panel will show them.
        break;
      }
    }
    setShowCollegeForm(false);
  }

  function handleUpdate(data: RetirementEventCreate) {
    if (!editingEvent) return;
    const updateData: RetirementEventUpdate = {
      name: data.name,
      eventType: data.eventType,
      amountCents: data.amountCents,
      year: data.year,
      endYear: data.endYear,
    };
    updateMutation.mutate(
      { id: editingEvent.id, data: updateData },
      {
        onSuccess: () => {
          setShowForm(false);
          setEditingEvent(undefined);
        },
      }
    );
  }

  function handleDelete(id: string) {
    const confirmed = window.confirm(
      'Are you sure you want to delete this event?'
    );
    if (confirmed) {
      deleteMutation.mutate(id);
    }
  }

  function handleEdit(event: RetirementEvent) {
    setEditingEvent(event);
    setShowForm(true);
  }

  function handleCancel() {
    setShowForm(false);
    setEditingEvent(undefined);
  }

  // ---- Render ----

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
        {/* Retirement is the primary goal and lives above this panel. These are
            the other two, kept visible rather than hidden behind a menu - their
            undiscoverability is the problem this work package exists to fix. */}
        <h3 className="text-sm font-medium text-gray-700">Also plan for</h3>
        {!showForm && !showCollegeForm && (
          <div className="flex items-center gap-2">
            {/* WP-RETIRE-13: recurring cash_flow events could already model
                college costs, but nothing in the product said so. This is the
                discoverable entry point to that capability. */}
            <button
              type="button"
              onClick={() => {
                setEditingEvent(undefined);
                setShowCollegeForm(true);
              }}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-primary-700 bg-primary-50 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              College
            </button>
            <button
              type="button"
              onClick={() => {
                setEditingEvent(undefined);
                setShowForm(true);
              }}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-primary-700 bg-primary-50 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Another life event
            </button>
          </div>
        )}
      </div>

      {/* WP-RETIRE-13: College plan generates ordinary cash_flow events */}
      {showCollegeForm && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="text-sm font-medium text-gray-700 mb-3">
            Plan for college
          </h3>
          <CollegePlanForm
            onSubmit={handleCreateCollegePlan}
            onCancel={() => setShowCollegeForm(false)}
            isSubmitting={createMutation.isPending}
          />
        </div>
      )}

      {/* Event Form */}
      {showForm && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="text-sm font-medium text-gray-700 mb-3">
            {editingEvent ? 'Edit Event' : 'New Event'}
          </h3>
          <RetirementEventForm
            event={editingEvent}
            onSubmit={editingEvent ? handleUpdate : handleCreate}
            onCancel={handleCancel}
            isSubmitting={createMutation.isPending || updateMutation.isPending}
          />
        </div>
      )}

      {/* Events list */}
      {eventsLoading ? (
        <div className="animate-pulse space-y-3">
          <div className="h-12 bg-gray-100 rounded" />
          <div className="h-12 bg-gray-100 rounded" />
        </div>
      ) : events.length === 0 && !showForm ? (
        <p className="text-sm text-gray-500 text-center py-6">
          Nothing added yet. Use <strong>College</strong> to plan tuition for
          each child, or <strong>Another life event</strong> for anything else
          with a start and an end — a second home, a home sale, a wedding.
          Regular retirement income belongs in Assumptions.
        </p>
      ) : (
        <div>
          {/* Money coming in */}
          {incomeEvents.length > 0 && (
            <div className="mb-4">
              <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1 px-4">
                {MONEY_IN_HEADING}
              </h3>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                {incomeEvents.map((event) => (
                  <EventRow
                    key={event.id}
                    event={event}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    isDeleting={deleteMutation.isPending}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Money going out */}
          {expenseEvents.length > 0 && (
            <div>
              <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1 px-4">
                {MONEY_OUT_HEADING}
              </h3>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                {expenseEvents.map((event) => (
                  <EventRow
                    key={event.id}
                    event={event}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    isDeleting={deleteMutation.isPending}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
